/** The Onboarding page (`/onboarding`; plan §16): the client follows and steers its own easy
 *  onboarding — choose tours, the setup progress, the logo, then Ready and going live. One poll of
 *  the state drives every part (useOnboardingState). A user without `tour_operator.write` sees the
 *  same page read-only; once the account is live the page is the "you're live" view and offers no
 *  changes (the writes answer 409 `onboarding_complete`). */
import { Alert, Box, Button, Chip, CircularProgress, Grid, Stack, Step, StepLabel, Stepper, Typography } from '@mui/material';
import * as React from 'react';
import { Link as RouterLink } from 'react-router-dom';

import PageHeading from '../components/PageHeading';
import PageMeta from '../components/PageMeta';
import { ContextStore } from '../store/contextStore';
import { SYNC_PATH } from '../sync/routes';
import { errorCode, isClientError, OnboardingApiError, useOnboardingApi } from './api';
import ChooseTours from './ChooseTours';
import { actionErrorText, AttentionList, ProgressPanel, Section } from './components';
import GoLivePanel from './GoLivePanel';
import LogoCard from './LogoCard';
import { useOnboardingStatus } from './OnboardingStatus';
import { ImportingPanel, Receipt } from './Ready';
import SandboxSetup from './SandboxSetup';
import type { OnboardingState } from './types';
import { isStalled, useOnboardingState } from './useOnboardingState';
import WebhookSetup from './WebhookSetup';
import WhatsNext from './WhatsNext';

const STEPS = ['Set up', 'Choose tours', 'Import', 'Ready', 'Live'];

/** The step the onboarding is on (index into STEPS). */
export function stepOf(s: OnboardingState): number {
    if (s.go_live.status === 'live') {
        return 5;
    }
    switch (s.phase) {
        case 'setup':
            return 0;
        case 'choose':
            return 1;
        case 'importing':
            return 2;
        default:
            return 3;
    }
}

/** A page-level refusal: the page is not offered, or not to this user. */
function Refusal({ error }: { error: unknown }) {
    const code = errorCode(error);
    const status = error instanceof OnboardingApiError ? error.status : 0;
    let title = "We couldn't open your onboarding";
    let body = actionErrorText(error);
    if (code === 'not_easy') {
        title = 'There is no onboarding for this account';
        body = 'This page is for accounts set up through self-onboarding. Everything else in the dashboard works as usual.';
    } else if (status === 403) {
        title = "You don't have access to the onboarding";
        body = 'Ask an account admin to give you access to the account settings.';
    } else if (status === 404) {
        title = 'Your user is not linked to an account';
        body = 'Sign in with the user you created for your company.';
    }
    return (
        <Alert severity="info" data-testid="onboarding-refused" action={<Button component={RouterLink} to="/" color="inherit" size="small">Go to the dashboard</Button>}>
            <strong>{title}</strong>
            <Box>{body}</Box>
        </Alert>
    );
}

const OnboardingPage = () => {
    const api = useOnboardingApi();
    const { hasPerm } = React.useContext(ContextStore) as any;
    const { publish, refuse } = useOnboardingStatus();
    const { state, error, loading, reconnecting, afterAction } = useOnboardingState(api, { onState: publish, onError: refuse });

    const live = state?.go_live.status === 'live';
    const canWrite = !!hasPerm?.('tour_operator', 'write') && !live;
    const readOnly = !hasPerm?.('tour_operator', 'write');
    // Product / aux syncs of the booking system have their own page and permissions (backend D17).
    const canSync = !!(hasPerm?.('import', 'sync_products') || hasPerm?.('import', 'sync_aux'));
    const logoLane = state?.lanes.find((l) => l.key === 'logo');
    // Go-live calls answer the go-live status; the page re-reads the whole state after each (refused
    // or not: our team may have acted meanwhile) and polls fast for a while.
    const reread = React.useCallback(async <T,>(call: Promise<T>) => {
        try {
            return await call;
        } finally {
            afterAction();
        }
    }, [afterAction]);
    const requestGoLive = React.useCallback((note: string) => reread(api.requestGoLive(note)), [api, reread]);
    const withdrawGoLive = React.useCallback(() => reread(api.withdrawGoLive()), [api, reread]);

    // The booking webhook the client adds itself (manual setup): on Ready, and after going live until one arrived.
    const webhookSetup = state?.webhook_setup && !(live && state.webhook_setup.received) ? state.webhook_setup : null;

    let main: React.ReactNode = null;
    if (state) {
        if (live || state.phase === 'ready') {
            main = (
                <Stack spacing={2}>
                    {!live && (
                        <Box>
                            <Typography variant="h6" component="h2">Your account is ready</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '68ch', mt: 0.5 }}>
                                Check what we imported. Guests don&apos;t hear from us until you go live.
                            </Typography>
                        </Box>
                    )}
                    {webhookSetup && <WebhookSetup setup={webhookSetup} canSeeToken={!readOnly} />}
                    <GoLivePanel state={state} canWrite={canWrite} onRequest={requestGoLive} onWithdraw={withdrawGoLive} />
                    {/* The client's own setup steps (backend plan 2026-10-29-dashboard-whats-next-setup); after go-live
                        they move to the dashboard home's "Finish your setup" card. The logo card beside it is the
                        logo step here. */}
                    {!live && (
                        <WhatsNext
                            hideKeys={['logo']}
                            intro="A few more things make your messages and your guests' pages complete. Do them now or after you go live: the list stays on your dashboard home until they are done."
                        />
                    )}
                    <Receipt state={state} />
                </Stack>
            );
        } else if (state.phase === 'importing') {
            main = <ImportingPanel state={state} api={api} canWrite={canWrite} afterAction={afterAction} stalled={isStalled(state)} />;
        } else {
            main = <ChooseTours state={state} api={api} canWrite={canWrite} afterAction={afterAction} />;
        }
    }

    return (
        <Box sx={{ p: { xs: 2, sm: 3 }, pt: { xs: 2, sm: 2 }, maxWidth: 1280 }} data-testid="onboarding-page">
            <PageMeta title="Onboarding" />
            <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 2, rowGap: 1 }}>
                <PageHeading>Onboarding</PageHeading>
                {state && <Typography color="text.secondary">{state.company.name}</Typography>}
                {readOnly && state && <Chip size="small" variant="outlined" label="Read-only" data-testid="read-only-chip" />}
            </Stack>

            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={28} /></Box>
            )}

            {!state && error != null && (isClientError(error) ? (
                <Refusal error={error} />
            ) : (
                <Alert severity="warning" data-testid="onboarding-unreachable">
                    {`${actionErrorText(error)} We'll keep trying.`}
                </Alert>
            ))}

            {state && (
                <Stack spacing={2}>
                    <Stepper activeStep={stepOf(state)} alternativeLabel sx={{ display: { xs: 'none', sm: 'flex' } }} data-testid="onboarding-steps">
                        {STEPS.map((label) => <Step key={label}><StepLabel>{label}</StepLabel></Step>)}
                    </Stepper>

                    {reconnecting && (
                        <Alert severity="info" data-testid="onboarding-reconnecting">Reconnecting… the page shows the last state it read.</Alert>
                    )}
                    {readOnly && !live && (
                        <Alert severity="info" data-testid="read-only-note">
                            You can follow the onboarding here. Only an account admin can make changes.
                        </Alert>
                    )}
                    {!live && <AttentionList attention={state.attention} lanes={state.lanes} />}

                    <Grid container spacing={2}>
                        <Grid item xs={12} md={8}>{main}</Grid>
                        <Grid item xs={12} md={4}>
                            <Stack spacing={2}>
                                {!live && <LogoCard api={api} canWrite={canWrite} laneState={logoLane?.state} onChanged={afterAction} />}
                                {state.sandbox && <SandboxSetup sandbox={state.sandbox} phaseReady={state.phase === 'ready'} />}
                                <ProgressPanel lanes={state.lanes} />
                                {!live && canSync && (
                                    <Section title="Your booking system" testId="sync-link">
                                        <Typography variant="body2" color="text.secondary">
                                            Import more products, or read guides, pickup points and other details from your booking system.
                                        </Typography>
                                        <Button component={RouterLink} to={SYNC_PATH} size="small" sx={{ mt: 1 }}>Open booking-system sync</Button>
                                    </Section>
                                )}
                            </Stack>
                        </Grid>
                    </Grid>
                </Stack>
            )}
        </Box>
    );
};

export default OnboardingPage;
